import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import Image from "next/image";
import { shimmer, toBase64 } from "@/components/core/PlaceholderImages/PlaceholderImages";
import { getMediumEDGE_Url, getThumbEDGE_Url } from "@/common/images/utils";
import Slider from "react-slick";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
// DYNAMIC IMPORTS
const LightBoxImage = dynamic(() => import("./LightBoxImage"), { ssr: false });
// STYLE
import s from "./CarouselProduct.module.scss";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ImageWithFallback from "@/components/elements/ImageWithFallback/ImageWithFallback";

interface CarouselProductProps {
  product: any;
  selectedColor: any;
}

export default function CarouselProduct({ product, selectedColor }: CarouselProductProps) {
  const router = useRouter();
  // slider arrows
  const [arrows, setArrows] = useState(false);
  // ref assigned to slider.
  let sliderComponent;
  // Lightbox state, image name (used to create url) and open/close (true/false)
  const [lightboxState, setLightboxState] = useState({ isOpen: false, index: 0 });
  // Images state
  const [filteredImages, setFilteredImages] = useState(product?.Immagini || []);

  // Filter out all images associated with a deleted color (images are still present in DigitalOcean and in product JSON on DB)
  useEffect(() => {
    const filteredImages = product?.Immagini.filter((image) => product?.Colori?.includes(image.color) || "DEFAULT" == image.color);
    setFilteredImages(filteredImages || []);
  }, [product]);

  // Disable page scroll when lightbox is open
  useEffect(() => {
    if (lightboxState.isOpen) {
      disableBodyScroll(document.querySelector(".lightbox"));
    } else {
      enableBodyScroll(document.querySelector(".lightbox"));
    }
  }, [lightboxState]);

  // Before router back must close the lightbox in order to enable the scrollbar on previous pages
  useEffect(() => {
    router.beforePopState(({ url, as }) => {
      setLightboxState((prevState) => ((prevState.isOpen = false), { ...prevState }));
      return true;
    });
  }, [router]);

  function handleLightBox(image) {
    setLightboxState((prevState) => ((prevState.isOpen = true), { ...prevState }));
    setLightboxState((prevState) => ((prevState.index = imagesUrls.indexOf(image.url)), { ...prevState }));
  }

  // Change slider position when selected color changes
  useEffect(() => {
    // Colors in filteredImages array
    const colorsImages = filteredImages.map((img) => img.color);

    // Index of image associated to selected color
    const imageIndex = colorsImages.indexOf(selectedColor);

    // If color is not present in img.color, skip the change slider position if not change slider position when selected color changes
    imageIndex !== -1 && sliderComponent.slickGoTo(imageIndex);
  }, [selectedColor]);

  // Create an array with only images urls
  const imagesUrls = filteredImages.map((image) => {
    return image.url;
  });

  function NextArrow(props) {
    const { className, style, onClick } = props;
    return (
      <div
        onMouseOver={() => setArrows(true)}
        onMouseLeave={() => setArrows(false)}
        id="next-arrow"
        className={className}
        style={{ ...style, display: "block", right: "20px", top: "40%", transform: "scale(1.5)" }}
        onClick={onClick}
      />
    );
  }

  function PrevArrow(props) {
    const { className, style, onClick } = props;
    return (
      <div
        onMouseOver={() => setArrows(true)}
        onMouseLeave={() => setArrows(false)}
        id="prev-arrow"
        className={className}
        style={{ ...style, display: "block", left: "15px", top: "40%", zIndex: "999", transform: "scale(1.5)" }}
        onClick={onClick}
      />
    );
  }

  // Those are the settings of the carousel, customPaging represent the thumbnails
  const settings = {
    customPaging: function (i) {
      return (
        <a
          onMouseOver={() => {
            sliderComponent.slickGoTo(i);
            setLightboxState((prevState) => ((prevState.index = i), { ...prevState }));
          }}
          onClick={() => {
            sliderComponent.slickGoTo(i);
            setLightboxState((prevState) => ((prevState.index = i), { ...prevState }));
          }}
        >
          <Image
            unoptimized
            src={getThumbEDGE_Url(imagesUrls[i])}
            key={getThumbEDGE_Url(imagesUrls[i])}
            layout="responsive"
            alt={`thumb-${i}`}
            className={s.imageThumb}
            sizes="10vw"
            width={2}
            height={3}
            quality={30}
          />
        </a>
      );
    },
    dots: true,
    dotsClass: "thumb-product",
    infinite: true,
    slidesToShow: 2,
    slidesToScroll: 1,
    adaptiveHeight: true,
    arrows: arrows,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    lazyLoad: true,
    responsive: [
      {
        breakpoint: 1279.98,
        settings: {
          arrows: false,
          dots: true,
          dotsClass: `thumb-product ${s.thumbnail}`,
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: true,
          lazyLoad: true,
        },
      },
    ],
  };

  return (
    <>
      <div className={s.carousel_wrapper}>
        <Slider ref={(slider) => (sliderComponent = slider)} {...settings}>
          {filteredImages.length > 0 ? (
            filteredImages.map((image, index) => {
              return (
                <a
                  onMouseOver={() => {
                    setArrows(true);
                  }}
                  onMouseLeave={() => setArrows(false)}
                  key={image.url}
                  onClick={() => {
                    handleLightBox(image);
                  }}
                  className={`pe-xl-2 ${s.divImage}`}
                >
                  <ImageWithFallback
                    className={s.image}
                    alt={product?.DescrizioneArticolo || "Product Image"}
                    width={768}
                    height={1152}
                    layout="responsive"
                    src={getMediumEDGE_Url(image.url)}
                    sizes="(max-width: 576px) 100vw, 50vw"
                    placeholder="blur"
                    priority={index == 0}
                  />
                </a>
              );
            })
          ) : (
            <ImageWithFallback
              className={s.image}
              alt={product?.DescrizioneArticolo || "Product Image"}
              width={768}
              height={1152}
              layout="responsive"
              src={"/static/images/product-img-placeholder.svg"}
              sizes="(max-width: 576px) 100vw, 50vw"
              placeholder="blur"
              priority={true}
            />
          )}
        </Slider>
      </div>
      <div className={`${s.lightbox_wrapper} lightbox`}>
        {lightboxState.isOpen && (
          <LightBoxImage enableZoom={true} imagesUrls={imagesUrls} lightboxState={lightboxState} setLightboxState={setLightboxState} />
        )}
      </div>
    </>
  );
}
